import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../../store";
import { SHOPKEEPER_STATUS } from "../../constants";
import { CheckCircle, Circle } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DataToolbar from "../../components/common/DataToolbar";
import DataTable from "../../components/common/DataTable";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";

import {
  setDateRange,
  setPage,
  setRowsPerPage,
  setSearchTerm,
  setSelectedRows,
  setSort,
} from "../../store/slices/tableSlice";
import { StatusChip } from "./utils";
import { useApproveRejectItemMutation, useLazyGetMaterialAvailabilityQuery } from "../../api/services/shopkeeperApi";
import Swal from "sweetalert2";
import dayjs from "dayjs";
// Request Table Component
const SpareTable: React.FC<{ data: any[]; status: string; isLoading: boolean }> = ({ data, status }) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    searchTerm,
    dateRange,
    rowsPerPage,
    currentPage,
    selectedRows,
    sortField,
    sortOrder,
  } = useSelector((state: any) => state.table);

  //Local States
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<"approve" | "reject" | null>(null);
  const [remarks, setRemarks] = useState("");
  const [remarksError, setRemarksError] = useState("");
  const [rowId, setRowId] = useState<number | null>(null);
  const isApprove = dialogType === "approve";

  //API
  const [approveRejectItem, { isLoading }] = useApproveRejectItemMutation();

  //API trigger functions
  const handleAction = async (type: "approve" | "reject", id: number, materialCode: string) => {
    setRowId(id);
    setDialogType(type);
    setRemarks("");
    setSelectedMaterial(materialCode); 
    setRemarksError("");
    setOpenDialog(true);
  };

  const handleApiTrigger = async (
    id: number | null,
    type: "approve" | "reject" | null
  ) => {
    if (!type || !id) return;

    if (type === "reject" && !remarks.trim()) {
      setRemarksError("Remarks is required for rejection");
      return;
    }
    try {
      await approveRejectItem({
        spareItemId: id,
        action: type,
        remarks,
        serialNumber: selectedSerial.serialNumber,
        valuationType:selectedSerial.valuationType
      }).unwrap();
      Swal.fire({
        icon: "success",
        title: "Success",
        text: `Item ${
          type === "approve" ? "approved" : "rejected"
        } successfully`,
        timer: 2000,
        showConfirmButton: false,
      });
      handleClose();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Something went wrong. Please try again.",
      });
    }
  };

  const handleClose = () => {
    setOpenDialog(false);
    setRemarks("");
  };

  const handleSelectedRows = (id: string) => {
    const newSelection = selectedRows.includes(id)
      ? selectedRows.filter((rowId: any) => rowId !== id)
      : [...selectedRows, id];
    dispatch(setSelectedRows(newSelection));
  };

  const handleSort = (field: string) => {
    dispatch(setSort({ field }));
  };

  const pendingCount = data.filter(
    (d) => d.status === SHOPKEEPER_STATUS.PENDING
  ).length;

  const subtitle =
    status === SHOPKEEPER_STATUS.PENDING
      ? "View all spare part requests approved by the manager that are pending your action or processing."
      : status === SHOPKEEPER_STATUS.ISSUED
      ? "See every easily submitted, a complete list for review and record keeping"
      : "";

  const filteredData = useMemo(() => {
    let result = [...data];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((row) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(term)
        )
      );
    }

    const [startDate, endDate] = dateRange;

    if (startDate || endDate) {
      result = result.filter((row) => {
        const requestDate = dayjs(row.Requested_Date);

        if (startDate && requestDate.isBefore(dayjs(startDate), "day")) {
          return false;
        }

        if (endDate && requestDate.isAfter(dayjs(endDate), "day")) {
          return false;
        }

        return true;
      });
    }
    return result;
  }, [data, searchTerm, dateRange]);

  // const sortedData = useMemo(() => {
  //   if (!sortField || !sortOrder) return filteredData;

  //   return [...filteredData].sort((a, b) => {
  //     const aVal = a[sortField];
  //     const bVal = b[sortField];

  //     if (aVal == null) return 1;
  //     if (bVal == null) return -1;

  //     if (sortField === "Requested_Date") {
  //       return sortOrder === "asc"
  //         ? new Date(aVal).getTime() - new Date(bVal).getTime()
  //         : new Date(bVal).getTime() - new Date(aVal).getTime();
  //     }

  //     if (typeof aVal === "number") {
  //       return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
  //     }

  //     return sortOrder === "asc"
  //       ? String(aVal).localeCompare(String(bVal))
  //       : String(bVal).localeCompare(String(aVal));
  //   });
  // }, [filteredData, sortField, sortOrder]);

  // const paginatedData = useMemo(() => {
  //   const startIndex = (currentPage - 1) * rowsPerPage;
  //   const endIndex = startIndex + rowsPerPage
  //   return sortedData.slice(startIndex, endIndex);
  // },[sortedData, currentPage, rowsPerPage])

  const columns = [
    { key: "Spare_ID", label: "spare Id", sortable: true },
    { key: "Requested_By", label: "Requested By", sortable: true },
    {
      key: "Requested_Date",
      label: "Requested On",
      sortable: true,
      render: (row: any) => (
        <Typography sx={{ fontSize: "0.8rem" }}>
          {dayjs(row.Requested_Date.split("+")[0]).format("DD/MM/YYYY, hh:mm A")}
        </Typography>
      ),
    },
    { key: "Material_Name", label: "Material Name", sortable: true },
    { key: "Material_Code", label: "Material Code", sortable: true },
    { key: "Quantity_requested", label: "Quantity", sortable: true },
    { key: "Availability", label: "Availability", sortable: true , render: (row: any) => (
      <Typography sx={{ fontSize: "0.8rem" , color: row.Availability === 0 ? "error.main" : "success.main" }}>
        {row.Availability !== null ? row.Availability : 'N/A'}
      </Typography>
    ),},
    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <StatusChip
          status={row.status}
          icon={<Circle sx={{ fontSize: 8 }} />}
          label={row.status}
          size="small"
        />
      ),
    },
  ];
const [selectedMaterial, setSelectedMaterial] = useState<string | null>(null);
const [serials, setSerials] = useState<any[]>([]);
const [selectedSerial, setSelectedSerial] = useState({serialNumber:"",valuationType:""});

const [getMaterialAvailability] =
  useLazyGetMaterialAvailabilityQuery();

  useEffect(() => {
  if (!openDialog || !selectedMaterial) return;

  (async () => {
    try {
      const res = await getMaterialAvailability(selectedMaterial).unwrap();
      setSerials(res); // API response array
    } catch (err) {
      Swal.fire("Error", "Failed to load serial numbers", "error");
    }
  })();
}, [openDialog, selectedMaterial]);

useEffect(() => {
  if (openDialog) {
    setSelectedSerial({ serialNumber: "", valuationType: "" });
  }
}, [openDialog]);

const handleSerialChange = (serialNumber: string) => {
  const selected = serials.find(
    (s) => (s.SerialNumber || "") === serialNumber
  );

  setSelectedSerial({
    serialNumber: serialNumber || "",
    valuationType: selected?.ValuationType || ""
  });
};
useEffect(() => {
  if (serials.length === 1) {
    const s = serials[0];

    setSelectedSerial({
      serialNumber: s.SerialNumber || "",
      valuationType: s.ValuationType || ""
    });
  }
}, [serials]);


  return (
    <Paper elevation={0} sx={{ backgroundColor: "#f5f5f5" }}>
      {(status === SHOPKEEPER_STATUS.PENDING ||
        status === SHOPKEEPER_STATUS.ISSUED) && (
        <Box sx={{ px: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {status === SHOPKEEPER_STATUS.PENDING
              ? `Pending request (${pendingCount})`
              : "Approved request"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        </Box>
      )}

      {/* search + filter */}
      <DataToolbar
        searchTerm={searchTerm}
        setSearchTerm={(val) => dispatch(setSearchTerm(val))}
        dateRange={dateRange}
        setDateRange={(val) => dispatch(setDateRange(val))}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={(val) => dispatch(setRowsPerPage(val))}
        currentPage={currentPage}
        setCurrentPage={(val) => dispatch(setPage(val))}
        totalPages={Math.max(
          1,
          Math.ceil((filteredData?.length ?? 0) / Math.max(1, rowsPerPage)),
        )}
      />
      <DataTable
        columns={columns}
        data={data}
        getRowId={(row) => row.Spare_ID}
        onSort={handleSort}
        sortField={sortField}
        sortOrder={sortOrder}
        showCheckbox
        selectedRows={selectedRows}
        onSelectRow={handleSelectedRows}
        actionRenderer={(row) =>
          row.status === SHOPKEEPER_STATUS.PENDING && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#002B5B",
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2,
                  fontSize: "0.75rem",
                }}
                disabled={row.Availability === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAction("approve", row.Spare_ID, row.Material_Code);
                }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 2,
                  fontSize: "0.75rem",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAction("reject", row.Spare_ID, row.Material_Code);
                }}
              >
                Reject
              </Button>
            </Box>
          )
        }
      />

      {/* Approval/ Rejection Popup */}
      <Dialog
        open={openDialog}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "visible",
            p: 1,
          },
        }}
      >
        {/* Top gradient and icon */}
        <Box
          sx={{
            position: "relative",
            height: 70,
            background: "linear-gradient(180deg, #eaf0fb 0%, #FFFFFF 100%)",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 60,
              height: 60,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isApprove ? (
              <CheckCircle sx={{ fontSize: 32, color: "#2E7D32" }} />
            ) : (
              <HighlightOffOutlinedIcon
                sx={{ fontSize: 32, color: "#D32F2F" }}
              />
            )}
          </Box>
        </Box>

        <Box>
          <DialogTitle sx={{ textAlign: "center", p: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              Confirm
              <Box
                component="span"
                sx={{ color: isApprove ? "success.main" : "error.main", ml: 1 }}
              >
                {isApprove ? "Approval" : "Rejection"} of Request
              </Box>
            </Typography>
          </DialogTitle>
          <Typography variant="body2" color="text.secondary" sx={{ px: 5 }}>
            The request will be{" "}
            {isApprove ? SHOPKEEPER_STATUS.ISSUED : SHOPKEEPER_STATUS.REJECTED}.
            The operator and manager will be notified.
          </Typography>
        </Box>
        <DialogContent sx={{ py: 1 }}>
          {/* <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            {dialogType === "reject" && (
              <span style={{ color: "red" }}> *</span>
            )}
          </Typography> */}
          {dialogType === "approve" && serials.length > 0 && (
            <>
            <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            Choose Serial number{" "} <span style={{ color: "red" }}> *</span>
          </Typography>
          <Select
            value={selectedSerial.serialNumber}
            onChange={(e) => handleSerialChange(e.target.value as string)}
            displayEmpty
            sx={{ mb: 2, width: "100%", bgcolor: "white" }}
          >
            <MenuItem value="">Choose a Serial number</MenuItem>

            {serials.map((s) => (
              <MenuItem key={s.SerialNumber} value={s.SerialNumber}>
                {(s.SerialNumber && s.SerialNumber.trim()) || "Stock without Serial"} - {s.TotalStock}
              </MenuItem>
            ))}
          </Select>
          </>
          )}
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            Remarks{" "}
            {dialogType === "reject" && (
              <span style={{ color: "red" }}> *</span>
            )}
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: ramnathdhawan"
            value={remarks}
            onChange={(e) => {
              setRemarks(e.target.value);
              if (remarksError) setRemarksError("");
            }}
            multiline
            minRows={3}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
          <Button
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              borderColor: "grey.400",
              color: "text.primary",
              px: 7,
              fontSize: "0.875rem",
            }}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={isLoading}
            disabled={dialogType === "reject" && !remarks.trim()}
            sx={{
              textTransform: "none",
              backgroundColor: "#002B5B",
              borderRadius: 2,
              px: 7,
              fontSize: "0.875rem",
            }}
            onClick={() => handleApiTrigger(rowId, dialogType)}
          >
            Proceed
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
export default SpareTable;
